import BasketItem from "./BaketItem";
import SliderItem from "./SliderItem";
import {useQuery} from "@tanstack/react-query";
import {getProducts} from "../../api/catalog";
import {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import {BasketItemInterface} from "../../interfaces/checkout/BasketItemInterface";
import {BasketInterface} from "../../interfaces/checkout/BasketInterface";
import {LOCAL_STORAGE_KEY_BASKET_LIST} from "../../Config";
import useLocalStorage from "../../utils/useLocalStorage";
import {CreateOrderInterface} from "../../interfaces/checkout/CreateOrderInterface";
import {createOrder as createOrderApi} from "../../api/customer";
import {CustomerAccountInterface} from "../../interfaces/customer/CustomerAccountInterface";
import {CustomerConsigneeInterface} from "../../interfaces/customer/CustomerConsigneeInterface";
import TotalBlock from "./TotalBlock";
import {getWordPositionEnding} from "../../utils/Utils";
import BreadcrumbInterface from "../../interfaces/core/BreadcrumbInterface";

export default function Basket() {

    const SHOW_POSITIONS_LIMIT = 10;

    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const navigate = useNavigate();

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [basketItems, setBasketItems] = useState<BasketItemInterface[]>([]);
    const [baskets, setBaskets] = useState<BasketInterface[]>([]);
    const [editedBasket, setEditedBasket] = useState<BasketInterface | null>(null);
    const [isMoveDropDownOpen, setIsMoveDropDownOpen] = useState<boolean>(false);

    const [selectedAccount, setSelectedAccount] = useState<CustomerAccountInterface | null>(null);
    const [selectedConsignee, setSelectedConsignee] = useState<CustomerConsigneeInterface | null>(null);
    const [comment, setComment] = useState<string>('');

    const [showAllPositions, setShowAllPositions] = useState<boolean>(false);

    const basketPositionsWithStock = state.basket.filter(item => item.product.has_client_stock);


    const {getObject: getLocalStorageObject, setObject: setLocalStorageObject} = useLocalStorage()


    const {data: response, status} = useQuery({
        queryKey: [state.basket, state.customer?.id],
        queryFn: () => getProducts({id: state.basket.map(item => item.product.id.toString())}, state.customer),
        //@ts-ignore
        retry: !window.NOSCRIPT ? false : 1,
        enabled: state.basket.length > 0,
    })

    useEffect(() => {
        let breadcrumbs: BreadcrumbInterface[] = [];

        breadcrumbs.push({
            name: 'Корзина',
        })
        dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: breadcrumbs})
    }, []);


    useEffect(() => {
        setBasketItems(state.basket.map(item => {
            item.product = response?.products.find(prod => prod.id === item.product.id) || item.product;
            return item;
        }))
    }, [response?.products]);

    const noStockCount = basketItems.filter(basketItem => !basketItem.product.has_client_stock).length;

    useEffect(() => {
        setBaskets(getLocalStorageObject(LOCAL_STORAGE_KEY_BASKET_LIST) || []);
    }, []);

    useEffect(() => {
        if (!state.basket.length) {
            let newBaskets = baskets.map(basket => ({
                ...basket,
                isActive: false
            }));
            const firstActiveBasket = newBaskets.find(basket => basket.items.length > 0);
            if (firstActiveBasket) {
                firstActiveBasket.isActive = true
            }
            setBasketsWithLocalStorage([...newBaskets])
            setBasketItems(firstActiveBasket?.items || [])
        }
    }, [state.basket]);

    const setBasketsWithLocalStorage = (newBaskets: BasketInterface[]) => {
        setBaskets(newBaskets);
        setLocalStorageObject(LOCAL_STORAGE_KEY_BASKET_LIST, newBaskets);
    }

    const saveBasket = () => {
        if (!editedBasket) {
            return
        }
        if (!editedBasket.uid) {
            editedBasket.uid = (new Date()).getTime().toString();
        }

        editedBasket.createdAt = new Date().toISOString();
        editedBasket.isActive = true


        const newBaskets = [...baskets.map(basket => {
            return {
                ...basket,
                isActive: false
            }
        }).filter(basket => basket.uid !== editedBasket?.uid), editedBasket!]

        setBasketsWithLocalStorage(newBaskets)
        setEditedBasket(null);
    }

    const movePositions = (basketUid: string) => {
        if (selectedItems.length === 0) {
            return
        }
        const itemsToMove = basketItems.filter(basketItem => selectedItems.includes(basketItem.product.id));
        const newCurrentBasketItems = basketItems.filter(basketItem => !selectedItems.includes(basketItem.product.id));
        const targetBasket = baskets.find(basket => basket.uid === basketUid);
        if (!targetBasket) return
        const newTargetBasketItems = [...targetBasket.items, ...itemsToMove].filter((item, index, self) => index === self.findIndex((t) => (t.product.id === item.product.id
        )));
        const newBaskets = baskets.map(basket => {
            if (basket.uid === basketUid) {
                return {
                    ...basket,
                    items: newTargetBasketItems
                }
            }
            if (basket.isActive) {
                return {
                    ...basket,
                    items: newCurrentBasketItems
                }
            }
            return basket;
        })
        setBasketsWithLocalStorage(newBaskets);
        dispatch({type: 'SET_BASKET', payload: newCurrentBasketItems});
        setSelectedItems([]);
        setIsMoveDropDownOpen(false)
    }

    const getBasketItemsWithStock = () => {
        return state.basket.filter(item => item.product.has_client_stock);
    }

    const createOrder = () => {
        if (!state.customer) {
            dispatch({type: ReducerActionType.ERROR, payload: 'Для оформления заказа необходимо авторизоваться'});
            return;
        }
        if (!selectedAccount) {
            dispatch({type: ReducerActionType.ERROR, payload: 'Пожалуйста, выберите лицевой счет'});
            return;
        }
        if (!selectedConsignee) {
            dispatch({type: ReducerActionType.ERROR, payload: 'Пожалуйста, выберите грузополучателя'});
            return;
        }
        // Proceed to create order
        const order: CreateOrderInterface = {
            customer: {
                id: state.customer.id,
                sign: state.customer.sign,
            },
            account: selectedAccount,
            consignee: selectedConsignee,
            comment: comment,
            products: getBasketItemsWithStock().map(item => ({
                id: item.product.id,
                customer_order_count: item.quantity,
            })),
        }

        // createOrderFake(order).then(response => {
        createOrderApi(order).then(response => {
            if (response && response.order?.id) {
                const basket  = state.basket;

                response.order.positions = response.order.positions.map((position) => {
                    const basketPosition = basket.find(item => item.product.id === position.product.id);
                    position.price_from_basket = basketPosition?.product.current_price;
                    position.cnt_from_basket = basketPosition?.quantity || 0;
                    return position
                })

                dispatch({type:ReducerActionType.CLEAR_BASKET});

                navigate(`/order/success/${response.order.id}`, {
                    state: { orderResponse: response } 
                });
            } else {
                dispatch({type: ReducerActionType.ERROR, payload: 'Ошибка при создании заказа'});
            }
        }).catch((error) => {
            dispatch({type: ReducerActionType.ERROR, payload: 'Ошибка при создании заказа:' + (error.response?.data?.message || error.message) });
        });

    }

    if (!basketItems.length) {
        return <div className="basket text-center">
            <div className="container">
                <h1>
                    Корзина пуста
                </h1>
                <p className="mt-3">Добавьте товары в корзину, чтобы оформить заказ.</p>
            </div>
        </div>
    }

    return (<>
        <div className={"modal " + (editedBasket && 'show')}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="d-flex flex-column modal-content">
                    <div className="d-flex flex-column modal-box modal-header">
                        <button type="button" className="btn-close"
                                aria-label="Закрыть" onClick={() => setEditedBasket(null)}></button>
                        <div className="fw-bold modal-title" id="modalVerticallyCenteredLabel">Название корзины
                        </div>
                    </div>
                    <div className="d-flex flex-column modal-box modal-body">
                        <div className="form-control-item">
                            <input type="text" className="form-control" value={editedBasket?.name} onChange={(e) => {
                                setEditedBasket({...editedBasket!, name: e.target.value})
                            }} placeholder="Название"/>
                        </div>

                    </div>
                    <div className="d-flex flex-column modal-box modal-footer">
                        <button type="button" className="btn btn-primary modal-btn two" onClick={() => {
                            saveBasket()
                        }}>{editedBasket?.uid ? 'Сохранить' : 'Создать'}</button>
                    </div>
                </div>
            </div>
        </div>

        <div className="basket">
            <div className="container">
                <h1 className="compar__title">
                    Корзина <span className={'text-danger'}>{baskets.find(item => item.isActive)?.name || ''}</span>
                </h1>
                <div className="compar__head">
                    <div className="d-flex flex-wrap compar__head-row">
                        <div className="compar__points pointer" onClick={() => setEditedBasket({
                            uid: undefined,
                            name: '',
                            isActive: false,
                            createdAt: new Date().toISOString(),
                            items: [...basketItems]
                        })}>
                            <span>Новая корзина</span>
                            <div className="plus">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 1V13M13 7H1" stroke="black" stroke-linecap="round"
                                          stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                        {baskets.map(basketItem => <div
                            className={"compar__points " + (!basketItem.isActive && 't-gray')}>
                            <span className={'pointer'} onClick={() => {
                                dispatch({type: 'SET_BASKET', payload: basketItem.items})
                                let newBaskets = [...baskets.map((basket: BasketInterface) => ({
                                    ...basket,
                                    isActive: basketItem.uid === basket.uid
                                }))];
                                setBasketsWithLocalStorage(newBaskets)
                            }}>{basketItem.name} ({basketItem.items.length})</span>
                            <div className="fix" onClick={() => {
                                setEditedBasket(basketItem);
                            }}>
                                <svg width="17" height="16" viewBox="0 0 17 16" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 12.9998V14.7928C1 14.9254 1.05268 15.0526 1.14645 15.1464C1.24021 15.2401 1.36739 15.2928 1.5 15.2928H3.293C3.42535 15.2928 3.55229 15.2403 3.646 15.1468L12.146 6.64682L9.646 4.14682L1.146 12.6468C1.05253 12.7405 1.00003 12.8675 1 12.9998ZM11.59 2.20282L14.09 4.70282L15.293 3.49982C15.4805 3.31229 15.5858 3.05798 15.5858 2.79282C15.5858 2.52765 15.4805 2.27335 15.293 2.08582L14.207 0.999817C14.0195 0.812346 13.7652 0.707031 13.5 0.707031C13.2348 0.707031 12.9805 0.812346 12.793 0.999817L11.59 2.20282Z"
                                        stroke="black"/>
                                </svg>
                            </div>
                            <div className="close" onClick={() => {
                                setBasketsWithLocalStorage(baskets.filter(basket => basket.uid !== basketItem.uid))
                            }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 11L1 1M11 1L1 11" stroke="#292929" stroke-linecap="round"/>
                                </svg>
                            </div>
                        </div>)}
                    </div>
                    <div
                        className="d-flex flex-column justify-content-between align-items-lg-center basket__head-row flex-lg-row">
                        <div className="d-flex align-items-center left-row">
                            <label className="form-check filter__t-min gray ">
                                <input className="form-check-input" type="checkbox" onClick={() => {
                                    if (selectedItems.length === basketItems.filter(basketItem => basketItem.product.has_client_stock).length) {
                                        setSelectedItems([]);
                                    } else {
                                        setSelectedItems(basketItems.filter(basketItem => basketItem.product.has_client_stock).map(basketItem => basketItem.product.id));
                                    }
                                }}
                                />
                                <div className="form-check-icon"></div>
                                <span className="form-check-text">
                                <span>Выбрать все товары</span>
                            </span>
                            </label>
                            {selectedItems.length > 0 && <div className="compar__points btn-points" onClick={() => {
                                dispatch({
                                    type: 'SET_BASKET',
                                    payload: state.basket.filter(basketItem => !selectedItems.includes(basketItem.product.id))
                                });
                                setSelectedItems([]);
                            }}>
                                <span>Удалить выбранное</span>
                                <div className="delete">
                                    <svg width="16" height="18" viewBox="0 0 16 18" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M1 18V3H0V1H5V0H11V1H16V3H15V18H1ZM3 16H13V3H3V16ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                                            fill="#292929"/>
                                    </svg>
                                </div>
                            </div>}
                        </div>
                        <div className="d-flex align-items-start align-items-md-center right-row">
                            {selectedItems.length > 0 && <div className="dropdown basket__move">
                                <button className="compar__points btn-points dropdown-toggle" type="button"
                                        onClick={() => {
                                            setIsMoveDropDownOpen(!isMoveDropDownOpen)
                                        }}>
                                    <span>Переместить ({selectedItems.length})</span>
                                    <div className="move">
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M10.6923 10L19 10M19 10L15.5385 13.4615M19 10L15.5385 6.53846M2.38461 19L5.84615 19C6.21338 19 6.56556 18.8541 6.82522 18.5945C7.08489 18.3348 7.23077 17.9826 7.23077 17.6154L7.23077 2.38461C7.23077 2.01739 7.08489 1.66521 6.82522 1.40554C6.56556 1.14588 6.21338 1 5.84615 1L2.38461 1C2.01739 1 1.66521 1.14588 1.40554 1.40554C1.14588 1.66521 1 2.01739 1 2.38461L1 17.6154C1 17.9826 1.14588 18.3348 1.40554 18.5945C1.66521 18.8541 2.01739 19 2.38461 19Z"
                                                stroke="#292929" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                    </div>
                                </button>
                                {isMoveDropDownOpen &&
                                    <ul className={"dropdown-menu show"} aria-labelledby="dropdownBasket">
                                        {baskets.filter(basket => !basket.isActive).map(basket => <li><span
                                            className="dropdown-item pointer" onClick={() => {
                                            if (!basket.uid) return
                                            movePositions(basket.uid)
                                        }}>{basket.name} ({basket.items.length})</span></li>)}
                                    </ul>}
                            </div>}
                            <div
                                className="d-flex flex-column flex-md-row align-items-md-center align-items-end basket__head-l-row">
                                <a href="#" className="d-flex align-items-center bs-link basket__link-head">
                                    <span>Поделиться</span>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M9.08341 1.20563C9.45732 0.825727 9.90273 0.523606 10.394 0.316688C10.8852 0.10977 11.4125 0.00214915 11.9456 3.18615e-05C12.4786 -0.00208543 13.0068 0.101343 13.4996 0.304352C13.9925 0.50736 14.4403 0.805934 14.8172 1.18285C15.1941 1.55977 15.4927 2.00758 15.6957 2.50045C15.8987 2.99333 16.0021 3.52151 16 4.05456C15.9979 4.5876 15.8902 5.11494 15.6833 5.60619C15.4764 6.09743 15.1743 6.54286 14.7944 6.91677L12.2297 9.48158L11.0319 8.28551L13.5967 5.71986C14.0367 5.28007 14.284 4.68349 14.2841 4.06138C14.2843 3.43926 14.0373 2.84256 13.5975 2.40254C13.1577 1.96252 12.5612 1.71524 11.9391 1.71508C11.317 1.71492 10.7203 1.9619 10.2803 2.40169L7.71471 4.96735L6.51783 3.77044L9.08341 1.20563ZM10.8779 6.31832L6.29437 10.9011L5.09749 9.7042L9.68016 5.12141L10.8779 6.31832ZM4.94428 7.737L2.37954 10.3027C1.93953 10.7427 1.69234 11.3395 1.69234 11.9617C1.69234 12.584 1.93953 13.1808 2.37954 13.6208C2.81954 14.0608 3.41632 14.308 4.03858 14.308C4.66084 14.308 5.25761 14.0608 5.69762 13.6208L8.26236 11.0552L9.45924 12.2521L6.8945 14.8169C6.13717 15.5743 5.10998 15.9999 4.03888 16C2.96778 16.0001 1.94052 15.5747 1.18308 14.8173C0.425648 14.06 7.93811e-05 13.0327 1.11041e-08 11.9616C-7.93589e-05 10.8905 0.425337 9.8632 1.18266 9.10575L3.7474 6.54094L4.94428 7.737Z"
                                            fill="#292929"/>
                                    </svg>
                                </a>
                                <a href="#" className="d-flex align-items-center bs-link basket__link-head">
                                    <span>Скачать</span>
                                    <svg width="14" height="16" viewBox="0 0 14 16" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M10.1111 0L14 4V15.2064C13.9998 15.4169 13.9183 15.6188 13.7735 15.7676C13.6287 15.9164 13.4324 16 13.2277 16H0.772333C0.568072 15.9985 0.372569 15.9145 0.228058 15.766C0.0835473 15.6175 0.00162917 15.4165 0 15.2064V0.7936C0 0.3552 0.346111 0 0.772333 0H10.1111ZM7.93333 8L10.1111 4.8H8.24444L7 6.6288L5.75556 4.8H3.88889L6.06667 8L3.88889 11.2H5.75556L7 9.3712L8.24444 11.2H10.1111L7.93333 8Z"
                                            fill="#292929"/>
                                    </svg>
                                </a>
                                <a href="#" className="d-flex align-items-center bs-link basket__link-head">
                                    <span>Распечатать</span>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M3.6 4.50039V0.900391H14.4V4.50039H3.6ZM14.4 9.45039C14.655 9.45039 14.8689 9.36399 15.0417 9.19119C15.2145 9.01839 15.3006 8.80479 15.3 8.55039C15.2994 8.29599 15.213 8.08239 15.0408 7.90959C14.8686 7.73679 14.655 7.65039 14.4 7.65039C14.145 7.65039 13.9314 7.73679 13.7592 7.90959C13.587 8.08239 13.5006 8.29599 13.5 8.55039C13.4994 8.80479 13.5858 9.01869 13.7592 9.19209C13.9326 9.36549 14.1462 9.45159 14.4 9.45039ZM5.4 15.3004H12.6V11.7004H5.4V15.3004ZM3.6 17.1004V13.5004H0V5.40039H18V13.5004H14.4V17.1004H3.6Z"
                                            fill="black"/>
                                    </svg>
                                </a>
                            </div>

                        </div>

                    </div>
                </div>
                <div className="d-flex flex-column flex-lg-row basket__content">
                    <div className="d-flex flex-column basket__card-row">
                        {basketPositionsWithStock.slice(0, showAllPositions ? Infinity : SHOW_POSITIONS_LIMIT).map(basketItem => <div
                            className="d-flex basket__card-content">
                            <label className="form-check">
                                <input className="form-check-input" type="checkbox"
                                       checked={selectedItems.includes(basketItem.product.id)} onClick={() => {
                                    if (selectedItems.includes(basketItem.product.id)) {
                                        setSelectedItems(selectedItems.filter(id => id !== basketItem.product.id))
                                    } else {
                                        setSelectedItems([...selectedItems, basketItem.product.id])
                                    }
                                }}/>
                                <div className="form-check-icon"></div>
                            </label>
                            <BasketItem basketItem={basketItem}/>
                        </div>)}
                        {basketItems.filter(basketItem => basketItem.product.has_client_stock).length > SHOW_POSITIONS_LIMIT && !showAllPositions &&
                            <div className="d-flex justify-content-center mt-3">
                                <span  className={'text-underline-dashed pointer'} onClick={(e) => {
                                    e.preventDefault()
                                    setShowAllPositions(true)
                                }}>Показать еще {basketPositionsWithStock.length - SHOW_POSITIONS_LIMIT} {getWordPositionEnding(basketPositionsWithStock.length - SHOW_POSITIONS_LIMIT)}
                                </span>
                            </div>}
                        {state.customer && <div className="d-flex flex-column if-basket__item">
                            <div className="d-flex align-items-center if-basket__l-row">
                                <p className="t-gray">Комментарий к заказу</p>
                            </div>
                            <textarea
                                className="form-control"
                                rows={5}
                                placeholder="Введите комментарий к заказу..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                        </div>}


                        {noStockCount > 0 && <div className="div">
                            <div className="fw-bold basket__card-title">
                                Недоступно для заказа
                            </div>
                            {basketItems.filter(basketItem => !basketItem.product.has_client_stock).map(basketItem => <div
                                className="d-flex basket__card-content"><BasketItem basketItem={basketItem}/></div>)}
                        </div>}

                    </div>
                    <TotalBlock
                        selectedAccount={selectedAccount}
                        setSelectedAccount={setSelectedAccount}
                        selectedConsignee={selectedConsignee}
                        setSelectedConsignee={setSelectedConsignee}
                        createOrder={createOrder}
                    />
                </div>
                <div className="basket__slider">
                    <div className="basket__wrapper swiper-wrapper">
                        <SliderItem/>
                        <SliderItem/>
                        <SliderItem/>
                        <SliderItem/>
                    </div>
                </div>
            </div>
        </div>
    </>)
}